import { Plugin } from 'ckeditor5/src/core';
import { getClassNames } from './util';
import VideoPropertiesCommand from './video-properties-command';

const VIDEOPROPERTIES = 'videoProperties';
const VIDEOPOSITION = 'videoPosition';
const VIDEOSIZE = 'videoSize';
const VIDEOOPTIONS = 'videoOptions';
const DEFAULTCLASS = 'media';

export default class VideoPropertiesEditing extends Plugin {
	static get pluginName() {
		return 'VideoPropertiesEditing';
	}

	constructor(editor) {
		super(editor);
	}

	init() {
		const editor = this.editor;
		const conversion = editor.conversion;

		editor.commands.add(
			VIDEOPROPERTIES,
			new VideoPropertiesCommand(editor)
		);

		conversion.for('editingDowncast').add((dispatcher) => {
			dispatcher.on('attribute:htmlContent', autoplayConverter);
			dispatcher.on('attribute:videoPosition', converter);
			dispatcher.on('attribute:videoSize', converter);
			dispatcher.on('attribute:videoOptions', videoConverterEditMode);
			dispatcher.on('attribute:poster', videoPosterConverter);
			dispatcher.on('attribute:source', sourceConverter);
		});

		conversion.for('dataDowncast').add((dispatcher) => {
			dispatcher.on('attribute:videoPosition', converter);
			dispatcher.on('attribute:videoSize', converter);
			dispatcher.on('attribute:videoOptions', videoConverterDataMode);
			dispatcher.on('attribute:poster', videoPosterConverter);
			dispatcher.on('attribute:source', sourceConverter);
		});

		function autoplayConverter(evt, data, conversionApi) {
			if (!conversionApi.consumable.consume(data.item, evt.name)) return;

			if (data.item.name !== 'htmlVideo') return;

			const attributes = data.item.getAttribute('htmlAttributes');

			if (attributes && attributes.attributes && attributes.attributes.hasOwnProperty('autoplay')) {
				const viewWriter = conversionApi.writer;
				const element = conversionApi.mapper.toViewElement(data.item);
				const videoElement = element ? element.getChild(0) : null;

				if (videoElement.name !== 'video')
					return;

				const attributes = videoElement.getAttributes();
				let hasAutoPlay = false;

				for (const a of attributes) {
					if (a[0] === 'autoplay') {
						hasAutoPlay = true;
						break;
					}
				}

				if (hasAutoPlay) {
					// rename autoplay to data-autoplay
					viewWriter.removeAttribute('autoplay', videoElement);
					viewWriter.setAttribute('data-autoplay', '', videoElement);
				}
			}
		}

		function converter(evt, data, conversionApi) {
			if (!conversionApi.consumable.consume(data.item, evt.name)) {
				return;
			}
			const viewWriter = conversionApi.writer;
			const element = conversionApi.mapper.toViewElement(data.item);
			const existingClasses = element.getClassNames();

			for (const c of existingClasses) {
				if (c === 'ck-widget') continue;

				viewWriter.removeClass(c, element);
			}

			// add classes
			const videoPosition = data.item.getAttribute(VIDEOPOSITION);
			const videoSize = data.item.getAttribute(VIDEOSIZE);
			const classes = [DEFAULTCLASS, videoPosition, videoSize];

			for (const c of classes) {
				if (c) {
					viewWriter.addClass(c, element);
				}
			}
		}

		function sourceConverter(evt, data, conversionApi) {
			if (!conversionApi.consumable.consume(data.item, evt.name)) {
				return;
			}
			const newValue = data.attributeNewValue;
			const viewWriter = conversionApi.writer;
			const element = conversionApi.mapper.toViewElement(data.item);
			let videoElement = element.name === 'video' ? element : null;

			if (!videoElement) {
				if (element.childCount === 0) {
					videoElement = data.item.getChild(0);
				} else {
					videoElement = element.getChild(0);
					// depending on first time add or after refresh, the element location changes thanks to html-support plugin.
					if (videoElement.name !== 'video')
						videoElement = videoElement.getChild(0);
				}
			}

			if (!videoElement) return;

			if (videoElement.name === 'video') {
				// handle via DOM
				const dom =
					editor.editing.view.domConverter.mapViewToDom(
						videoElement
					);
				if (!dom) return;

				const source = dom.querySelector('source');
				source.setAttribute('src', newValue);

				// update parent div source
				let parent = videoElement.parent;

				// depending if this is the first load or refresh, the parent location changes
				if (parent.name !== 'div') {
					parent = parent.parent;
				}
				viewWriter.setAttribute('data-source', newValue, parent);
			} else {
				// htmlVideo handle via model
				const sourceHtml = `<source src="${newValue}">`;
				viewWriter.setAttribute(
					'htmlContent',
					sourceHtml,
					videoElement
				);

				// update parent div source
				const parent = videoElement.parent;
				const attributes = parent.getAttribute('htmlAttributes');
				const sourceAttribute = parent.getAttribute('data-source');

				if (attributes && attributes.attributes) {
					attributes.attributes.source = newValue;
					viewWriter.setAttribute(
						'htmlAttributes',
						attributes,
						parent
					);
				}

				if (sourceAttribute) {
					viewWriter.setAttribute('data-source', newValue, parent);
				}
			}
		}

		function videoPosterConverter(evt, data, conversionApi) {
			if (!conversionApi.consumable.consume(data.item, evt.name)) {
				return;
			}
			const newValue = data.attributeNewValue;
			const viewWriter = conversionApi.writer;
			const element = conversionApi.mapper.toViewElement(data.item);
			// finding the video element
			let videoElement = element.name === 'video' ? element : null;

			if (!videoElement) {
				if (element.childCount === 0) {
					videoElement = data.item.getChild(0);
				} else {
					videoElement = element.getChild(0);
					// depending on first time add or after refresh, the element location changes thanks to html-support plugin.
					if (videoElement.name !== 'video')
						videoElement = videoElement.getChild(0);
				}
			}

			if (videoElement) {
				viewWriter.setAttribute('poster', newValue, videoElement);
				const attributes = videoElement.getAttribute('htmlAttributes');

				if (attributes) {
					attributes.attributes.poster = newValue;
					viewWriter.setAttribute(
						'htmlAttributes',
						attributes,
						videoElement
					);
				}
			}
		}

		function videoConverter(evt, data, conversionApi, dataMode) {
			if (!conversionApi.consumable.consume(data.item, evt.name)) {
				return;
			}

			const newValue = data.attributeNewValue;
			const viewWriter = conversionApi.writer;
			let element = conversionApi.mapper.toViewElement(data.item);

			if (element.childCount === 0) {
				element = data.item;
				const videoElement = element.getChild(0);

				if (!videoElement) return;
				const newAttributes = newValue.split(',');
				const newAttributesObj = {};

				for (const a of newAttributes) {
					let attr =
						dataMode && a === 'data-autoplay' ? 'autoplay' : a;
					newAttributesObj[attr] = '';
				}
				let poster = videoElement.getAttribute('poster');

				if (!poster) {
					const htmlAttributes =
						videoElement.getAttribute('htmlAttributes');
					poster = htmlAttributes
						? htmlAttributes.attributes.poster
						: null;
				}

				if (poster) newAttributesObj['poster'] = poster;

				viewWriter.setAttribute(
					'htmlAttributes',
					{ attributes: newAttributesObj },
					videoElement
				);
			} else {
				let videoElement = element.getChild(0);
				// depending on first time add or after refresh, the element location changes thanks to html-support plugin.
				if (videoElement.name !== 'video')
					videoElement = videoElement.getChild(0);

				const attributes = videoElement.getAttributes();

				for (const a of attributes) {
					// remove all attributes except poster
					if (a[0] === 'poster') {
						continue;
					}

					viewWriter.removeAttribute(a[0], videoElement);
				}
				const newAttributes = newValue.split(',');

				for (const a of newAttributes) {
					let attr =
						dataMode && a === 'data-autoplay' ? 'autoplay' : a;
					viewWriter.setAttribute(attr, '', videoElement);
				}
			}
		}

		function videoConverterEditMode(evt, data, conversionApi) {
			videoConverter(evt, data, conversionApi, false);
		}

		function videoConverterDataMode(evt, data, conversionApi) {
			videoConverter(evt, data, conversionApi, true);
		}
	}
}
